import { State, Action, StateContext, Selector } from '@ngxs/store';
import { SnackbarConfig, SnackbarType } from '@core/models';
import {
  EnableMobileModeAction,
  DisableMobileModeAction,
  OpenSnackbarAction,
  CloseSnackbarAction,
  ShowSpinnerAction,
  HideSpinnerAction,
  OpenMenuAction,
  CloseMenuAction,
  SetThemeAction,
  ToggleIsInferiorBrowserAction,
  UpdateSignalRConnectionStatusAction,
  RegisterSignalRHandlersForUiAction,
  UpdateIsMobileMode,
} from './ui.actions';
import { AnimationsService } from '@core/animations/animations.service';
import { Injectable } from '@angular/core';
import { SignalRService } from '@core/services/signalr.service';

export class UiStateModel {
  public showMenu: boolean;
  public theme: string;
  public isMobileMode: boolean;
  public showSpinner: boolean;
  public signalRConnected: boolean;
  public signalRHandlersRegistered: boolean;
  public isInferiorBrowser: boolean;
  public snackbarConfig: SnackbarConfig;
}

@State<UiStateModel>({
  name: 'ui',
  defaults: {
    showMenu: true,
    theme: 'light-theme',
    isMobileMode: false,
    showSpinner: false,
    signalRConnected: false,
    signalRHandlersRegistered: false,
    isInferiorBrowser: false,
    snackbarConfig: { show: false }
  }
})
@Injectable()
export class UiState {
  constructor(
    private animationsService: AnimationsService,
    public signalRService: SignalRService
  ) { }

  @Selector()
  static getShowMenu(state: UiStateModel) {
    return state.showMenu;
  }

  @Selector()
  static getIsInferiorBrowser(state: UiStateModel) {
    return state.isInferiorBrowser;
  }

  @Selector()
  static getTheme(state: UiStateModel) {
    return state.theme;
  }

  @Selector()
  static getIsMobileMode(state: UiStateModel) {
    return state.isMobileMode;
  }

  @Selector()
  static getSignalRConnectionState(state: UiStateModel) {
    return state.signalRConnected;
  }

  @Selector()
  static getShowSpinner(state: UiStateModel) {
    return state.showSpinner;
  }

  @Selector()
  static getSnackbarConfig(state: UiStateModel) {
    return state.snackbarConfig;
  }

  @Action(EnableMobileModeAction)
  enableMobileModeAction(ctx: StateContext<UiStateModel>, action: EnableMobileModeAction) {
    ctx.patchState({
      isMobileMode: true
    });
  }

  @Action(OpenMenuAction)
  openMenuAction(ctx: StateContext<UiStateModel>, action: OpenMenuAction) {
    ctx.patchState({
      showMenu: true
    });
    localStorage.setItem('menuState', 'true');
  }

  @Action(CloseMenuAction)
  closeMenuAction(ctx: StateContext<UiStateModel>, action: CloseMenuAction) {
    ctx.patchState({
      showMenu: false
    });
    localStorage.setItem('menuState', 'false');
  }

  @Action(DisableMobileModeAction)
  disableMobileModeAction(ctx: StateContext<UiStateModel>, action: DisableMobileModeAction) {
    ctx.patchState({
      isMobileMode: false
    });
  }

  @Action(ToggleIsInferiorBrowserAction)
  toggleIsInferiorBrowserAction(ctx: StateContext<UiStateModel>, action: ToggleIsInferiorBrowserAction) {
    this.animationsService.updateRouteAnimationType(!action.payload.isInferiorBrowser, !action.payload.isInferiorBrowser);

    ctx.patchState({
      isInferiorBrowser: action.payload.isInferiorBrowser
    });
  }

  @Action(SetThemeAction)
  enableDarkThemeAction(ctx: StateContext<UiStateModel>, action: SetThemeAction) {
    ctx.patchState({
      theme: action.payload.theme
    });
  }

  @Action(OpenSnackbarAction)
  openSnackbarAction(ctx: StateContext<UiStateModel>, action: OpenSnackbarAction) {
    ctx.patchState({
      snackbarConfig: { show: true, message: action.payload.message, type: action.payload.type, problemDetail: action.payload.problemDetail }
    });
  }

  @Action(CloseSnackbarAction)
  closeSnackbarAction(ctx: StateContext<UiStateModel>, action: CloseSnackbarAction) {
    ctx.patchState({
      snackbarConfig: { show: false }
    });
  }

  @Action(ShowSpinnerAction)
  showSpinnerAction(ctx: StateContext<UiStateModel>, action: ShowSpinnerAction) {
    ctx.patchState({
      showSpinner: true
    });
  }

  @Action(HideSpinnerAction)
  hideSpinnerAction(ctx: StateContext<UiStateModel>, action: HideSpinnerAction) {
    ctx.patchState({
      showSpinner: false
    });
  }

  @Action(UpdateSignalRConnectionStatusAction)
  updateSignalRConnectionStatusAction(ctx: StateContext<UiStateModel>, action: UpdateSignalRConnectionStatusAction) {
    ctx.patchState({
      signalRConnected: action.payload.connected
    });
  }

  @Action(UpdateIsMobileMode)
    updateIsMobileMode(ctx: StateContext<UiStateModel>, action: UpdateIsMobileMode) {
    const state = ctx.getState();
    ctx.setState({
        ...state,
        isMobileMode: action.isMobileMode,
    });
    }


  @Action(RegisterSignalRHandlersForUiAction)
  registerSignalRHandlersForUiAction({ getState, dispatch, patchState }: StateContext<UiStateModel>, action: RegisterSignalRHandlersForUiAction) {
    const state = getState();

    if (!state.signalRHandlersRegistered) {
      this.signalRService.getHubConnection().on('SendSnackbarMessage', (message: string) => {
        dispatch(new OpenSnackbarAction({ type: SnackbarType.Simple, message }));
      });

      patchState({
        signalRHandlersRegistered: true
      });
    }
  }
}
